import React from 'react';
import { RadioGroup as CRadioGroup, RadioGroupProps as CRadioGroupProps, useRadioGroup } from '@chakra-ui/react';
import classNames from 'classnames';
import { Colors } from '@/styles/colors';
import { Button, ButtonGroup } from '../Button';
import { Radio } from './Radio';
import styles from './RadioGroup.module.scss';

export interface RadioGroupProps<T = string> extends Omit<CRadioGroupProps, 'children'> {
  buttonClassName?: string;
  buttonColor?: ValueOf<typeof Colors>;
  containerClassName?: string;
  type: 'button' | 'radio' | 'div';
  options: { value: T; children: React.ReactNode; disabled?: boolean }[];
}

export const RadioGroup = <T extends string = string>({
  containerClassName,
  buttonClassName,
  buttonColor,
  type,
  options,
  defaultValue,
  onChange = () => {},
  ...props
}: RadioGroupProps<T>) => {
  if (type === 'button') {
    const { value, setValue } = useRadioGroup({ defaultValue, onChange });
    return (
      <CRadioGroup defaultValue={defaultValue} {...props}>
        <ButtonGroup className={classNames(styles.container, containerClassName)} isAttached w="100%">
          {options.map((option) => (
            <Button
              as="label"
              key={String(option.value)}
              className={classNames(
                styles.button,
                { [styles.selected]: option.value === value, [styles.disabled]: option.disabled },
                buttonClassName,
              )}
              style={{ backgroundColor: option.value === value ? buttonColor || Colors.Sienna : undefined }}
              disabled={option.disabled}
              onClick={
                option.disabled
                  ? undefined
                  : () => {
                      setValue(option.value as string);
                      onChange(option.value);
                    }
              }
            >
              {option.children}
            </Button>
          ))}
        </ButtonGroup>
      </CRadioGroup>
    );
  }

  if (type === 'radio') {
    return (
      <CRadioGroup defaultValue={defaultValue} onChange={onChange} {...props}>
        {options.map((option) => (
          <Radio key={String(option.value)} value={option.value as string} isDisabled={option.disabled}>
            {option.children}
          </Radio>
        ))}
      </CRadioGroup>
    );
  }

  if (type === 'div') {
    return (
      <CRadioGroup defaultValue={defaultValue} onChange={onChange} {...props}>
        <div className={classNames(styles.selectContainer, containerClassName)}>
          {options.map((option) => (
            <div
              key={String(option.value)}
              className={classNames(styles.selectOption, 'rounded-md', {
                [styles.selected]: option.value === defaultValue,
                [styles.disabled]: option.disabled,
              })}
              onClick={
                option.disabled
                  ? undefined
                  : () => {
                      onChange(option.value);
                    }
              }
            >
              {option.children}
            </div>
          ))}
        </div>
      </CRadioGroup>
    );
  }

  return null;
};
